import path from 'node:path';
import { pathToFileURL } from 'node:url';
import { parseSnapshot } from '@chatsdet/browser-core';
import type { SelectorStrategy } from '@chatsdet/types';
import type { TraceAction, TraceSnapshotPhase } from '@chatsdet/trace-tools';
import type { ElementHandle, Locator, Page } from 'playwright';
import { buildLocator, type BuildLocatorResult } from '../../build-locator.js';
import { resolveElement } from '../../element-resolver.js';
import { evaluateLocatorChain } from '../../locator-parser.js';
import { hydrateDynamicSelectors } from '../../selector-derivation.js';
import { LocatorVerifier, classifyStrategyTokens, type LocatorVerification } from '../locator-verifier.js';
import { waitForTraceDomHydration, type SubstrateMaterializer } from '../substrate-materializer.js';
import type { HeadlessDomSession } from '../headless-session.js';
import type { GroundTruthReasonCode } from './decision-log.js';

export type PrimaryTarget =
  | { kind: 'row-strategy'; strategy: SelectorStrategy; variableValues: Record<string, string> }
  | { kind: 'runcode-chain'; locatorChain: string }
  | { kind: 'trace-selector'; selector: string };

export interface DiscoverTargetOptions {
  materializer: SubstrateMaterializer;
  session: HeadlessDomSession;
  actionIndex: number;
  traceAction: TraceAction;
  primary: PrimaryTarget;
  viewport?: { width: number; height: number };
}

export type DiscoverTargetResult =
  | {
      ok: true;
      ref: string;
      substrateYaml: string;
      domPath: string;
      confirmations: string[];
      signals?: string[];
    }
  | { ok: false; reason: GroundTruthReasonCode; detail?: string };

export interface AuthorAndVerifyOptions {
  ref: string;
  substrateYaml: string;
  domPath: string;
  variableValues?: Record<string, string>;
  verifier?: LocatorVerifier;
  session?: HeadlessDomSession;
}

export type AuthorAndVerifyResult =
  | {
      ok: true;
      strategy: SelectorStrategy;
      predicate: string;
      bindings: BuildLocatorResult['bindings'];
      verification: LocatorVerification;
    }
  | { ok: false; reason: GroundTruthReasonCode; detail?: string };

export interface VerifyOnRunOptions {
  verifier: LocatorVerifier;
  targetRef: string;
  variableValues?: Record<string, string>;
}

export interface PreviewSignal {
  tagName: string;
  name?: string;
  text?: string;
}

export interface StructuralPathElement {
  tagName: string;
  attributes?: Record<string, string | null | undefined>;
  parent?: StructuralPathElement | null;
}

export interface StructuralPathSegment {
  tagName: string;
  attrs: Array<{ name: string; value: string }>;
}

export interface SyntheticSignalOutcome {
  name: string;
  // 'agree-tag-only' — the signal verified only the element's tag (e.g. a
  // Playwright-ellipsized preview with no comparable name/text). It never
  // conflicts, but it is too weak to stand as the sole confirmation: a stale
  // same-tag element would satisfy it.
  // 'iframe' — the point resolved to an iframe/frame element: the true target
  // likely lives inside a frame the reconstruction cannot see, so this is a
  // hard stop, not an ordinary disagreement.
  status: 'agree' | 'agree-tag-only' | 'conflict' | 'missing' | 'iframe';
}

export interface ActualPreviewSignal {
  tagName: string;
  name?: string;
  text?: string;
}

export type SyntheticGroundTruthDecision =
  | { ok: true; confirmations: string[] }
  | { ok: false; reason: Extract<GroundTruthReasonCode, 'GT_UNCONFIRMED_SINGLE_SIGNAL' | 'GT_SIGNAL_CONFLICT'> };

export interface PlaywrightTargetMarkerSummary {
  tagName: string;
  text?: string;
}

export type MarkerPrimaryRelation =
  | { status: 'related'; marker: string; primary: string }
  | { status: 'different'; marker: string; primary: string }
  | { status: 'missing'; marker: string };

export type MarkerPrimaryAgreementDecision =
  | { ok: true; signal: 'gt_primary:agree' | 'gt_primary:missing' }
  | { ok: false; reason: Extract<GroundTruthReasonCode, 'GT_SIGNAL_CONFLICT'>; detail: string };

export interface MethodCompatibilityInput {
  method: string;
  tagName: string;
  editableControl: boolean;
}

export type MethodCompatibilityDecision =
  | { ok: true }
  | { ok: false; reason: Extract<GroundTruthReasonCode, 'GT_SIGNAL_CONFLICT'>; detail: string };

export type PendingRowCandidateOutcome =
  | { actionIndex: number; status: 'match' }
  | { actionIndex: number; status: 'miss'; reason: string; detail?: string }
  | { actionIndex: number; status: 'inconclusive'; reason: string; detail?: string };

export type PendingRowCandidateDecision =
  | { ok: true; actionIndex: number }
  | { ok: false; detail: string };

export type MarkerLookupDecision =
  | { status: 'fallback' }
  | { status: 'found' }
  | { status: 'gap'; reason: Extract<GroundTruthReasonCode, 'GT_MARKER_AMBIGUOUS'>; detail: string };

const RESOLVE_TIMEOUT_MS = 5_000;
const PLAYWRIGHT_TARGET_SELECTOR = '[__playwright_target__]';
const STRUCTURAL_ATTRS = [
  'data-testid',
  'data-test',
  'data-cy',
  'name',
  'aria-label',
  'placeholder',
  'role',
  'type',
  'id',
] as const;

export function parseResolvedElementPreview(preview: string | undefined): PreviewSignal | null {
  if (!preview) return null;
  const trimmed = preview.trim();
  if (!trimmed) return null;

  const htmlMatch = /^<([a-zA-Z][\w:-]*)([^>]*)>([\s\S]*?)<\/\1>$/.exec(trimmed);
  if (htmlMatch) {
    const [, tagName, attrs, inner] = htmlMatch;
    const name =
      extractHtmlAttr(attrs, 'aria-label') ??
      extractHtmlAttr(attrs, 'name') ??
      extractHtmlAttr(attrs, 'title') ??
      undefined;
    // Playwright elides long inner content to a literal U+2026 ellipsis
    // (trimStringWithEllipsis) — that means "content unknown", not a
    // comparable text value. ASCII "..." is real content and stays comparable.
    const rawText = normalizeText(inner.replace(/<[^>]*>/g, ' '));
    const text = rawText === '…' ? '' : rawText;
    return {
      tagName: tagName.toLowerCase(),
      ...(name ? { name } : {}),
      ...(text ? { text } : {}),
    };
  }

  const terseMatch = /^([a-zA-Z][\w:-]*)\s+["']([^"']+)["']/.exec(trimmed);
  if (terseMatch) {
    return {
      tagName: terseMatch[1]!.toLowerCase(),
      name: terseMatch[2],
      text: terseMatch[2],
    };
  }

  const tagOnly = /^([a-zA-Z][\w:-]*)\b/.exec(trimmed);
  return tagOnly ? { tagName: tagOnly[1]!.toLowerCase() } : null;
}

export function buildStableStructuralPath(element: StructuralPathElement): StructuralPathSegment[] {
  const reversed: StructuralPathSegment[] = [];
  let current: StructuralPathElement | null | undefined = element;
  while (current) {
    const attrs: Array<{ name: string; value: string }> = [];
    const sourceAttrs = current.attributes ?? {};
    for (const name of STRUCTURAL_ATTRS) {
      const value = sourceAttrs[name];
      if (typeof value === 'string' && value.trim()) {
        attrs.push({ name, value: value.trim() });
      }
    }
    reversed.push({ tagName: current.tagName.toLowerCase(), attrs });
    current = current.parent;
  }
  return reversed.reverse();
}

export function structuralPathToCss(pathSegments: StructuralPathSegment[]): string {
  return pathSegments
    .map((segment) => {
      const attrs = segment.attrs
        .map((attr) => `[${attr.name}="${escapeCssAttr(attr.value)}"]`)
        .join('');
      return `${segment.tagName}${attrs}`;
    })
    .join(' > ');
}

export function decideGroundTruthSignalOutcomes(
  outcomes: SyntheticSignalOutcome[]
): SyntheticGroundTruthDecision {
  if (outcomes.some((outcome) => outcome.status === 'conflict' || outcome.status === 'iframe')) {
    return { ok: false, reason: 'GT_SIGNAL_CONFLICT' };
  }
  // Tag-only agreements corroborate but never confirm on their own — a stale
  // same-tag element would satisfy them.
  const confirmations = outcomes
    .filter((outcome) => outcome.status === 'agree')
    .map((outcome) => outcome.name)
    .sort();
  if (confirmations.length === 0) {
    return { ok: false, reason: 'GT_UNCONFIRMED_SINGLE_SIGNAL' };
  }
  return { ok: true, confirmations };
}

export function decideMarkerLookup(
  markerCount: number,
  phase: TraceSnapshotPhase
): MarkerLookupDecision {
  if (markerCount === 0) return { status: 'fallback' };
  if (markerCount === 1) return { status: 'found' };
  return {
    status: 'gap',
    reason: 'GT_MARKER_AMBIGUOUS',
    detail: `${markerCount} elements matched ${PLAYWRIGHT_TARGET_SELECTOR} in ${phase} snapshot`,
  };
}

export function summarizePlaywrightTargetMarkersFromHtml(
  html: string
): PlaywrightTargetMarkerSummary[] {
  const out: PlaywrightTargetMarkerSummary[] = [];
  const pattern =
    /<([a-zA-Z][\w:-]*)(?=[^>]*\s__playwright_target__(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+))?)([^>]*)>([\s\S]*?)<\/\1>/g;
  for (const match of html.matchAll(pattern)) {
    const tagName = match[1]!.toLowerCase();
    const text = normalizeText(match[3]!.replace(/<[^>]*>/g, ' '));
    out.push({
      tagName,
      ...(text ? { text } : {}),
    });
  }
  return out;
}

export function decideMarkerPrimaryAgreement(
  relation: MarkerPrimaryRelation
): MarkerPrimaryAgreementDecision {
  if (relation.status === 'related') return { ok: true, signal: 'gt_primary:agree' };
  if (relation.status === 'missing') return { ok: true, signal: 'gt_primary:missing' };
  return {
    ok: false,
    reason: 'GT_SIGNAL_CONFLICT',
    detail: `Playwright marker ${relation.marker} disagreed with primary target ${relation.primary}`,
  };
}

export type PointConflictDecision =
  | { veto: true }
  | { veto: false; signal: 'gt_point:conflict-downgraded-sole-cross-check' };

/**
 * The point cross-check resolves live-run click coordinates against the
 * RECONSTRUCTED DOM, so it is layout-dependent: reconstruction layout shift
 * (fonts, viewport, lazy assets) moves elements and makes elementFromPoint
 * land on an unrelated node even when the marker is correct. It may therefore
 * only hard-veto when it is not the sole dissenting signal against the
 * generation-guarded marker: with a content-based primary selector in play
 * (`gt_primary:agree` — the recorded selector independently resolved to the
 * marker's element), a point conflict still gaps. With a content-free
 * selector (`gt_primary:missing`, e.g. aria-ref), the marker is the only
 * ground truth and the point disagreement is recorded as a signal instead —
 * the authored locator still has to survive the verifier close-loop.
 *
 * This function is only consulted for ordinary element disagreements: a point
 * resolving to an iframe/frame hard-stops upstream (GT_IFRAME_UNSUPPORTED)
 * before any downgrade, because that geometry is the cross-frame stale-marker
 * window where the point check is the last guard.
 */
export function decidePointConflict(
  primarySignal: 'gt_primary:agree' | 'gt_primary:missing'
): PointConflictDecision {
  if (primarySignal === 'gt_primary:missing') {
    return { veto: false, signal: 'gt_point:conflict-downgraded-sole-cross-check' };
  }
  return { veto: true };
}

export function decideMethodCompatibility(
  input: MethodCompatibilityInput
): MethodCompatibilityDecision {
  if (input.method === 'fill' || input.method === 'type') {
    return input.editableControl
      ? { ok: true }
      : {
          ok: false,
          reason: 'GT_SIGNAL_CONFLICT',
          detail: `${input.method} target must be an editable control, got <${input.tagName}>`,
        };
  }

  if (input.method === 'select' || input.method === 'selectOption') {
    return input.tagName === 'select'
      ? { ok: true }
      : {
          ok: false,
          reason: 'GT_SIGNAL_CONFLICT',
          detail: `${input.method} target must be a <select>, got <${input.tagName}>`,
        };
  }

  return { ok: true };
}

export function classifyPendingRowCandidateDiscovery(
  actionIndex: number,
  discovered: DiscoverTargetResult
): PendingRowCandidateOutcome {
  if (discovered.ok) {
    if (discovered.signals?.includes('gt_primary:missing')) {
      return {
        actionIndex,
        status: 'miss',
        reason: 'gt_primary:missing',
        detail: 'row strategy did not resolve uniquely on the candidate snapshot',
      };
    }
    return { actionIndex, status: 'match' };
  }

  return {
    actionIndex,
    status: 'inconclusive',
    reason: discovered.reason,
    ...(discovered.detail ? { detail: discovered.detail } : {}),
  };
}

export function decidePendingRowCandidateOutcomes(
  outcomes: PendingRowCandidateOutcome[],
  candidateCount: number
): PendingRowCandidateDecision {
  const matches = outcomes.filter((outcome) => outcome.status === 'match');
  const inconclusive = outcomes.filter((outcome) => outcome.status === 'inconclusive');
  if (inconclusive.length === 0 && matches.length === 1) {
    return { ok: true, actionIndex: matches[0]!.actionIndex };
  }
  return {
    ok: false,
    detail: `row strategy matched ${matches.length} of ${candidateCount} candidates; ${formatPendingRowCandidateOutcomes(outcomes)}`,
  };
}

export function decideResolvedPreviewSignal(
  preview: PreviewSignal,
  actual: ActualPreviewSignal
): SyntheticSignalOutcome {
  const tagMatches = actual.tagName.toLowerCase() === preview.tagName.toLowerCase();
  if (!tagMatches) return { name: 'resolved-preview', status: 'conflict' };

  const expectedName = normalizeText(preview.name ?? preview.text ?? '');
  if (!expectedName) return { name: 'resolved-preview', status: 'agree-tag-only' };

  const actualName = normalizeText(actual.name ?? '');
  const actualText = normalizeText(actual.text ?? '');
  if (actualName === expectedName || actualText === expectedName) {
    return { name: 'resolved-preview', status: 'agree' };
  }

  return { name: 'resolved-preview', status: 'missing' };
}

export async function discoverTarget(opts: DiscoverTargetOptions): Promise<DiscoverTargetResult> {
  const marker = await discoverTargetFromMarker(opts);
  if (marker.status === 'found') return marker.result;
  if (marker.status === 'gap') return marker.result;
  return discoverTargetByInference(opts);
}

async function discoverTargetByInference(opts: DiscoverTargetOptions): Promise<DiscoverTargetResult> {
  const before = await opts.materializer.materialize(opts.actionIndex, 'before').catch((error: unknown) => {
    const detail = error instanceof Error ? error.message : String(error);
    return { error: detail };
  });
  if ('error' in before) {
    return {
      ok: false,
      reason: 'GT_NO_BEFORE_SNAPSHOT',
      detail: `before snapshot unavailable: ${before.error}`,
    };
  }

  const page = await opts.session.page();
  try {
    if (opts.viewport) await page.setViewportSize(opts.viewport);
    await page.goto(pathToFileURL(path.resolve(before.domPath)).href, { waitUntil: 'load' });
    await waitForTraceDomHydration(page);

    const primary = await resolvePrimary(page, opts.primary);
    if (!primary) {
      return { ok: false, reason: 'GT_SIGNAL_CONFLICT', detail: 'primary target did not resolve uniquely' };
    }

    const primaryHandle = await primary.elementHandle({ timeout: RESOLVE_TIMEOUT_MS });
    if (!primaryHandle) {
      return { ok: false, reason: 'GT_SIGNAL_CONFLICT', detail: 'primary target handle missing' };
    }

    try {
      const ownerFrame = await primaryHandle.ownerFrame();
      if (ownerFrame && ownerFrame !== page.mainFrame()) {
        return { ok: false, reason: 'GT_IFRAME_UNSUPPORTED' };
      }

      const outcomes: SyntheticSignalOutcome[] = [];
      const pointOutcome = await confirmPoint(page, primaryHandle, opts.traceAction, opts.viewport);
      if (pointOutcome.reason) return pointOutcome.reason;
      if (pointOutcome.outcome) outcomes.push(pointOutcome.outcome);

      const fillOutcome = await confirmFillStructuralPath(opts, page, primaryHandle);
      if (fillOutcome) outcomes.push(fillOutcome);

      const previewOutcome = await confirmResolvedPreview(primaryHandle, opts.traceAction);
      if (previewOutcome) outcomes.push(previewOutcome);

      const decision = decideGroundTruthSignalOutcomes(outcomes);
      if (!decision.ok) {
        if (isFillLikeAction(opts.traceAction) && decision.reason === 'GT_UNCONFIRMED_SINGLE_SIGNAL') {
          return { ok: false, reason: 'GT_FILLFORM_FIELD_UNVERIFIED' };
        }
        return { ok: false, reason: decision.reason };
      }

      const substrateYaml = await page.ariaSnapshot({ mode: 'ai' });
      const ref = await refForHandle(page, primaryHandle, substrateYaml);
      if (!ref) {
        return { ok: false, reason: 'GT_SIGNAL_CONFLICT', detail: 'primary handle had no aria ref' };
      }
      return {
        ok: true,
        ref,
        substrateYaml,
        domPath: before.domPath,
        confirmations: decision.confirmations,
      };
    } finally {
      await primaryHandle.dispose().catch(() => undefined);
    }
  } finally {
    await page.close().catch(() => undefined);
  }
}

type MarkerDiscovery =
  | { status: 'found'; result: Extract<DiscoverTargetResult, { ok: true }> }
  | { status: 'gap'; result: Extract<DiscoverTargetResult, { ok: false }> }
  | { status: 'fallback' };

async function discoverTargetFromMarker(opts: DiscoverTargetOptions): Promise<MarkerDiscovery> {
  if (opts.traceAction.inputSnapshot) {
    const input = await markerDiscoveryForPhase(opts, 'input', { failMaterialize: true });
    if (input.status === 'found' || input.status === 'gap') return input;
    return { status: 'fallback' };
  }

  const after = await markerDiscoveryForPhase(opts, 'after', { failMaterialize: false });
  if (after.status === 'found' || after.status === 'gap') return after;
  return { status: 'fallback' };
}

async function markerDiscoveryForPhase(
  opts: DiscoverTargetOptions,
  phase: Extract<TraceSnapshotPhase, 'input' | 'after'>,
  policy: { failMaterialize: boolean }
): Promise<MarkerDiscovery> {
  const materialized = await opts.materializer.materialize(opts.actionIndex, phase).catch((error: unknown) => {
    const detail = error instanceof Error ? error.message : String(error);
    return { error: detail };
  });
  if ('error' in materialized) {
    if (!policy.failMaterialize || isMissingSnapshotError(materialized.error, phase)) {
      return { status: 'fallback' };
    }
    return {
      status: 'gap',
      result: {
        ok: false,
        reason: 'GT_NO_BEFORE_SNAPSHOT',
        detail: `${phase} snapshot unavailable: ${materialized.error}`,
      },
    };
  }

  const page = await opts.session.page();
  try {
    if (opts.viewport) await page.setViewportSize(opts.viewport);
    await page.goto(pathToFileURL(path.resolve(materialized.domPath)).href, { waitUntil: 'load' });
    await waitForTraceDomHydration(page);

    const marker = await markerHandle(page);
    const markerDecision = decideMarkerLookup(marker.count, phase);
    if (markerDecision.status === 'fallback') return { status: 'fallback' };
    if (markerDecision.status === 'gap') {
      return {
        status: 'gap',
        result: {
          ok: false,
          reason: markerDecision.reason,
          detail: markerDecision.detail,
        },
      };
    }
    if (marker.status !== 'found') return { status: 'fallback' };

    const markerSummary = await describeElementHandle(marker.handle);

    try {
      const ownerFrame = await marker.handle.ownerFrame();
      if (ownerFrame && ownerFrame !== page.mainFrame()) {
        return { status: 'gap', result: { ok: false, reason: 'GT_IFRAME_UNSUPPORTED' } };
      }

      const signals = [
        `gt_marker_phase:${phase}`,
        `gt_marker_tag:${markerSummary.tagName}`,
        ...(markerSummary.text ? [`gt_marker_text:${markerSummary.text}`] : []),
      ];

      const primaryDecision = decideMarkerPrimaryAgreement(
        await markerPrimaryRelation(page, marker.handle, opts.primary, markerSummary.label)
      );
      if (!primaryDecision.ok) {
        return {
          status: 'gap',
          result: {
            ok: false,
            reason: primaryDecision.reason,
            detail: primaryDecision.detail,
          },
        };
      }
      signals.push(primaryDecision.signal);

      const methodCompatibility = decideMethodCompatibility(
        await methodCompatibilityInput(marker.handle, opts.traceAction)
      );
      if (!methodCompatibility.ok) {
        return {
          status: 'gap',
          result: {
            ok: false,
            reason: methodCompatibility.reason,
            detail: methodCompatibility.detail,
          },
        };
      }

      const pointOutcome = await confirmPoint(page, marker.handle, opts.traceAction, opts.viewport);
      if (pointOutcome.reason) return { status: 'gap', result: pointOutcome.reason };
      if (pointOutcome.outcome) {
        signals.push(signalForOutcome(pointOutcome.outcome));
        if (pointOutcome.outcome.status === 'iframe') {
          // The click landed on an iframe: the marked main-frame element may
          // be a stale marker from an earlier action while the true target
          // lived in a frame the reconstruction cannot see. Never downgrade
          // this — it is the load-bearing guard for the cross-frame
          // stale-marker window on content-free selectors.
          return {
            status: 'gap',
            result: {
              ok: false,
              reason: 'GT_IFRAME_UNSUPPORTED',
              detail: 'trace point resolved to an iframe; target may live in an unreconstructed frame',
            },
          };
        }
        if (pointOutcome.outcome.status === 'conflict') {
          const pointDecision = decidePointConflict(primaryDecision.signal);
          if (pointDecision.veto) {
            return {
              status: 'gap',
              result: {
                ok: false,
                reason: 'GT_SIGNAL_CONFLICT',
                detail: 'trace point resolved to a different element than the Playwright target marker',
              },
            };
          }
          signals.push(pointDecision.signal);
        }
      }

      const previewOutcome = await confirmResolvedPreview(marker.handle, opts.traceAction);
      if (previewOutcome) signals.push(signalForOutcome(previewOutcome));

      const substrateYaml = await page.ariaSnapshot({ mode: 'ai' });
      const ref = await refForHandle(page, marker.handle, substrateYaml);
      if (!ref) {
        return {
          status: 'gap',
          result: { ok: false, reason: 'GT_SIGNAL_CONFLICT', detail: 'marker handle had no aria ref' },
        };
      }
      return {
        status: 'found',
        result: {
          ok: true,
          ref,
          substrateYaml,
          domPath: materialized.domPath,
          confirmations: [`marker:${phase}`],
          signals,
        },
      };
    } finally {
      await marker.handle.dispose().catch(() => undefined);
    }
  } finally {
    await page.close().catch(() => undefined);
  }
}

type MarkerHandleResult =
  | { status: 'missing'; count: 0 }
  | { status: 'ambiguous'; count: number }
  | { status: 'found'; count: 1; handle: ElementHandle<Element> };

async function markerHandle(page: Page): Promise<MarkerHandleResult> {
  const locator = page.locator(PLAYWRIGHT_TARGET_SELECTOR);
  const count = await locator.count().catch(() => 0);
  if (count === 0) return { status: 'missing', count };
  if (count > 1) return { status: 'ambiguous', count };
  const handle = await locator.elementHandle({ timeout: RESOLVE_TIMEOUT_MS });
  return handle ? { status: 'found', count: 1, handle } : { status: 'missing', count: 0 };
}

interface ElementSummary {
  tagName: string;
  text?: string;
  label: string;
}

async function describeElementHandle(handle: ElementHandle<Element>): Promise<ElementSummary> {
  const summary = await handle.evaluate((element) => {
    const attrs = ['id', 'data-testid', 'data-test', 'data-cy', 'aria-label', 'name', 'role', 'type'];
    const attrText = attrs
      .map((name) => {
        const value = element.getAttribute(name);
        return value ? `${name}="${value}"` : '';
      })
      .filter(Boolean)
      .join(' ');
    const text = (element.textContent ?? '').replace(/\s+/g, ' ').trim();
    return {
      tagName: element.tagName.toLowerCase(),
      attrText,
      text,
    };
  });
  const text = truncateForDetail(summary.text);
  const attrText = summary.attrText ? ` ${summary.attrText}` : '';
  const textLabel = text ? ` text="${text}"` : '';
  return {
    tagName: summary.tagName,
    ...(text ? { text } : {}),
    label: `<${summary.tagName}${attrText}${textLabel}>`,
  };
}

async function markerPrimaryRelation(
  page: Page,
  markerHandle: ElementHandle<Element>,
  primary: PrimaryTarget,
  markerLabel: string
): Promise<MarkerPrimaryRelation> {
  const primaryLocator = await resolvePrimary(page, primary);
  if (!primaryLocator) return { status: 'missing', marker: markerLabel };

  const primaryHandle = await primaryLocator
    .elementHandle({ timeout: RESOLVE_TIMEOUT_MS })
    .catch(() => null);
  if (!primaryHandle) return { status: 'missing', marker: markerLabel };

  try {
    const related = await page.evaluate(
      ([marker, primaryElement]) =>
        marker === primaryElement || marker.contains(primaryElement) || primaryElement.contains(marker),
      [markerHandle, primaryHandle] as const
    );
    const primarySummary = await describeElementHandle(primaryHandle);
    return related
      ? { status: 'related', marker: markerLabel, primary: primarySummary.label }
      : { status: 'different', marker: markerLabel, primary: primarySummary.label };
  } finally {
    await primaryHandle.dispose().catch(() => undefined);
  }
}

async function methodCompatibilityInput(
  handle: ElementHandle<Element>,
  traceAction: TraceAction
): Promise<MethodCompatibilityInput> {
  const target = await handle.evaluate((element) => {
    const tagName = element.tagName.toLowerCase();
    const editableControl =
      tagName === 'input' ||
      tagName === 'textarea' ||
      tagName === 'select' ||
      (element instanceof HTMLElement && element.isContentEditable);
    return { tagName, editableControl };
  });
  return {
    method: actionMethod(traceAction),
    tagName: target.tagName,
    editableControl: target.editableControl,
  };
}

function isMissingSnapshotError(detail: string, phase: TraceSnapshotPhase): boolean {
  return detail.startsWith(`No ${phase}-snapshot HTML reconstructable`);
}

function signalForOutcome(outcome: SyntheticSignalOutcome): string {
  return `gt_${outcome.name}:${outcome.status}`;
}

export async function refForHandle(
  page: Page,
  handle: ElementHandle<Element>,
  substrateYaml: string
): Promise<string | null> {
  const refs = parseRefsFromYaml(substrateYaml);
  // Bounded scan is a deliberate over-gap tradeoff: an unusually huge snapshot
  // can fail target discovery instead of spending unbounded time probing refs.
  for (const ref of refs.slice(0, 500)) {
    const candidate = await page
      .locator(`aria-ref=${ref}`)
      .elementHandle({ timeout: 250 })
      .catch(() => null);
    if (!candidate) continue;
    try {
      const same = await page.evaluate(([a, b]) => a === b, [handle, candidate] as const);
      if (same) return ref;
    } finally {
      await candidate.dispose().catch(() => undefined);
    }
  }
  return null;
}

export async function authorAndVerify(opts: AuthorAndVerifyOptions): Promise<AuthorAndVerifyResult> {
  const snapshot = parseSnapshot(opts.substrateYaml);
  const authored = buildLocator(snapshot, opts.ref, opts.variableValues ?? {});
  if (authored.irreducible) {
    return { ok: false, reason: 'AUTHOR_IRREDUCIBLE', detail: authored.predicate };
  }

  const tokenPass = classifyStrategyTokens(authored.selectors);
  if (!tokenPass.volatileTokenClean) {
    return {
      ok: false,
      reason: 'AUTHOR_VOLATILE_TOKENS',
      detail: tokenPass.volatileTokens.join(', '),
    };
  }

  const verifier = opts.verifier ?? new LocatorVerifier(opts.session ? { session: opts.session } : undefined);
  try {
    const verification = await verifier.verify({
      domPath: opts.domPath,
      targetRef: opts.ref,
      strategy: authored.selectors,
      variableValues: opts.variableValues,
    });
    if (!verification.resolvesUniquely) {
      return { ok: false, reason: 'VERIFY_FAILED_UNIQUENESS', detail: verification.reason };
    }
    if (!verification.matchesTarget) {
      return { ok: false, reason: 'VERIFY_FAILED_TARGET_MISMATCH', detail: verification.reason };
    }
    if (!verification.volatileTokenClean) {
      return { ok: false, reason: 'VERIFY_FAILED_VOLATILE', detail: verification.reason };
    }
    return {
      ok: true,
      strategy: authored.selectors,
      predicate: authored.predicate,
      bindings: authored.bindings,
      verification,
    };
  } finally {
    if (!opts.verifier && !opts.session) {
      await verifier.close().catch(() => undefined);
    }
  }
}

export async function verifyOnRun(
  domPath: string,
  strategy: SelectorStrategy,
  targetDiscovery: VerifyOnRunOptions
): Promise<LocatorVerification> {
  return targetDiscovery.verifier.verify({
    domPath,
    targetRef: targetDiscovery.targetRef,
    strategy,
    variableValues: targetDiscovery.variableValues,
  });
}

async function resolvePrimary(page: Page, primary: PrimaryTarget): Promise<Locator | null> {
  if (primary.kind === 'row-strategy') {
    const hydrated = hydrateDynamicSelectors(primary.strategy, primary.variableValues);
    return resolveElement(page, hydrated, RESOLVE_TIMEOUT_MS, { mode: 'verify' });
  }

  if (primary.kind === 'trace-selector') {
    const locator = page.locator(primary.selector);
    const count = await locator.count().catch(() => 0);
    return count === 1 ? locator : null;
  }

  const locator = evaluateLocatorChain(page, primary.locatorChain);
  const count = await locator.count().catch(() => 0);
  return count === 1 ? locator : null;
}

async function confirmPoint(
  page: Page,
  primaryHandle: ElementHandle<Element>,
  traceAction: TraceAction,
  viewport?: { width: number; height: number }
): Promise<{
  outcome?: SyntheticSignalOutcome;
  reason?: { ok: false; reason: GroundTruthReasonCode; detail?: string };
}> {
  const point = traceAction.point;
  if (!point) return {};
  if (viewport && (point.x < 0 || point.y < 0 || point.x >= viewport.width || point.y >= viewport.height)) {
    return {
      reason: {
        ok: false,
        reason: 'GT_POINT_OFFVIEWPORT',
        detail: `${point.x},${point.y} outside ${viewport.width}x${viewport.height}`,
      },
    };
  }

  // If the point lands on an iframe/frame, the real target likely lives
  // inside a frame the reconstruction cannot see — report it distinctly so
  // callers hard-stop instead of treating it as an ordinary disagreement.
  const pointOnFrame = await page.evaluate(({ x, y }) => {
    const tag = document.elementFromPoint(x, y)?.tagName.toLowerCase();
    return tag === 'iframe' || tag === 'frame';
  }, point);
  if (pointOnFrame) return { outcome: { name: 'point', status: 'iframe' } };

  const handle = await page.evaluateHandle(
    ({ x, y }) => {
      const node = document.elementFromPoint(x, y);
      return (
        node?.closest('button,a,input,select,textarea,[role],[tabindex],label,summary') ?? null
      );
    },
    point
  );
  const elementHandle = handle.asElement();
  if (!elementHandle) {
    await handle.dispose().catch(() => undefined);
    return { outcome: { name: 'point', status: 'missing' } };
  }

  try {
    const sameOrRelated = await page.evaluate(
      ([a, b]) => a === b || a.contains(b) || b.contains(a),
      [primaryHandle, elementHandle] as const
    );
    return { outcome: { name: 'point', status: sameOrRelated ? 'agree' : 'conflict' } };
  } finally {
    await elementHandle.dispose().catch(() => undefined);
    await handle.dispose().catch(() => undefined);
  }
}

async function confirmFillStructuralPath(
  opts: DiscoverTargetOptions,
  beforePage: Page,
  primaryHandle: ElementHandle<Element>
): Promise<SyntheticSignalOutcome | null> {
  const fillValue = traceFillValue(opts.traceAction);
  if (!fillValue) return null;

  const after = await opts.materializer.materialize(opts.actionIndex, 'after').catch(() => null);
  if (!after) return { name: 'fill-structural-path', status: 'missing' };

  const afterPage = await opts.session.page();
  try {
    await afterPage.goto(pathToFileURL(path.resolve(after.domPath)).href, { waitUntil: 'load' });
    await waitForTraceDomHydration(afterPage);
    const pathSegments = await structuralPathForControlValue(afterPage, fillValue);
    if (!pathSegments) return { name: 'fill-structural-path', status: 'missing' };

    const css = structuralPathToCss(pathSegments);
    const beforeLocator = beforePage.locator(css);
    const count = await beforeLocator.count().catch(() => 0);
    if (count !== 1) return { name: 'fill-structural-path', status: 'missing' };
    const beforeHandle = await beforeLocator.elementHandle({ timeout: RESOLVE_TIMEOUT_MS });
    if (!beforeHandle) return { name: 'fill-structural-path', status: 'missing' };
    try {
      const same = await beforePage.evaluate(([a, b]) => a === b, [primaryHandle, beforeHandle] as const);
      return { name: 'fill-structural-path', status: same ? 'agree' : 'conflict' };
    } finally {
      await beforeHandle.dispose().catch(() => undefined);
    }
  } finally {
    await afterPage.close().catch(() => undefined);
  }
}

async function confirmResolvedPreview(
  primaryHandle: ElementHandle<Element>,
  traceAction: TraceAction
): Promise<SyntheticSignalOutcome | null> {
  const preview = parseResolvedElementPreview(resolvedElementPreview(traceAction));
  if (!preview) return null;
  const actual = await primaryHandle.evaluate((element) => ({
    tagName: element.tagName.toLowerCase(),
    text: (element.textContent ?? '').replace(/\s+/g, ' ').trim(),
    name:
      element.getAttribute('aria-label') ??
      element.getAttribute('name') ??
      element.getAttribute('title') ??
      '',
  }));

  return decideResolvedPreviewSignal(preview, actual);
}

async function structuralPathForControlValue(
  page: Page,
  value: string
): Promise<StructuralPathSegment[] | null> {
  const paths = await page.locator('input, textarea, select').evaluateAll(
    (elements, expected) => {
      const attrs = [
        'data-testid',
        'data-test',
        'data-cy',
        'name',
        'aria-label',
        'placeholder',
        'role',
        'type',
        'id',
      ];
      const build = (element: Element): StructuralPathSegment[] => {
        const out: StructuralPathSegment[] = [];
        let current: Element | null = element;
        while (current) {
          out.push({
            tagName: current.tagName.toLowerCase(),
            attrs: attrs
              .map((name) => ({ name, value: current?.getAttribute(name) ?? '' }))
              .filter((attr) => attr.value.trim().length > 0),
          });
          current = current.parentElement;
        }
        return out.reverse();
      };
      return elements
        .filter((element) => {
          if (element instanceof HTMLInputElement) return element.value === expected;
          if (element instanceof HTMLTextAreaElement) return element.value === expected;
          if (element instanceof HTMLSelectElement) return element.value === expected;
          return false;
        })
        .map((element) => build(element));
    },
    value
  );
  return paths.length === 1 ? paths[0]! : null;
}

function parseRefsFromYaml(yaml: string): string[] {
  const refs = new Set<string>();
  for (const match of yaml.matchAll(/\[ref=([^\]\s]+)\]/g)) {
    refs.add(match[1]!);
  }
  return [...refs];
}

function resolvedElementPreview(action: TraceAction): string | undefined {
  const params = action.params ?? {};
  const value = params.resolvedElementPreview ?? params.elementPreview ?? params.preview;
  return typeof value === 'string' ? value : undefined;
}

function traceFillValue(action: TraceAction): string | null {
  if (!isFillLikeAction(action)) return null;
  const params = action.params ?? {};
  const value = params.value ?? params.text;
  return typeof value === 'string' ? value : null;
}

function isFillLikeAction(action: TraceAction): boolean {
  const method = actionMethod(action);
  return method === 'fill' || method === 'type';
}

function actionMethod(action: TraceAction): string {
  return action.title.split('.').pop() ?? action.title;
}

function formatPendingRowCandidateOutcomes(outcomes: PendingRowCandidateOutcome[]): string {
  if (outcomes.length === 0) return 'no candidate outcomes recorded';
  return outcomes
    .map((outcome) => {
      if (outcome.status === 'match') return `${outcome.actionIndex}=match`;
      const detail = outcome.detail ? `: ${truncateForDetail(outcome.detail)}` : '';
      return `${outcome.actionIndex}=${outcome.status}(${outcome.reason}${detail})`;
    })
    .join('; ');
}

function truncateForDetail(value: string): string {
  const normalized = normalizeText(value);
  return normalized.length > 120 ? `${normalized.slice(0, 117)}...` : normalized;
}

function extractHtmlAttr(attrs: string, name: string): string | null {
  const pattern = new RegExp(`${name}\\s*=\\s*["']([^"']+)["']`, 'i');
  return pattern.exec(attrs)?.[1] ?? null;
}

function normalizeText(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

function escapeCssAttr(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}
